import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    CaseServiceInfo,
    InsuranceCase,
    PolicyHolderDetails
} from '../../../app-create-case/models/insurance-case.model';
import { MatDialogRef } from '@angular/material/dialog';
import { SendConsultationCallBackRequestCommand } from '../../models/commandModels/SendConsultationCallBackRequestCommand';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { CommonService } from '../../services/common.service';
import { TranslateService } from '@ngx-translate/core';
import { ServiceProviderContactService } from '../../../app-create-case/services/service-provider-contact.service';

@Component({
    selector: 'app-send-consultation-call-back-request',
    templateUrl: './send-consultation-call-back-request.component.html',
    styleUrls: ['./send-consultation-call-back-request.component.scss'],
    standalone: false
})
export class SendConsultationCallBackRequestComponent implements OnInit, OnDestroy {
    insuranceCase: InsuranceCase;
    caseServiceInfo: CaseServiceInfo;
    policyHolderInfo: PolicyHolderDetails;

    consultationCallBackRequestForm: FormGroup;
    isLoading: boolean = false;
    isFromPolicyHolderInfo: boolean = false;
    private destroy$: Subject<boolean> = new Subject<boolean>();
    isAllowedToRequest: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data,
        private translateService: TranslateService,
        private commonService: CommonService,
        private matDialog: MatDialogRef<SendConsultationCallBackRequestComponent>,
        private readonly serviceProviderContactService: ServiceProviderContactService
    ) {}

    ngOnInit(): void {
        this.insuranceCase = this.data.insuranceCase;
        const currentConsultation = this.getCurrentConsultation();
        this.createFormGroup(currentConsultation);
        this.formFieldValidationUpdate();
    }

    private createFormGroup(formData: {
        Phone: string;
        Email: string;
        Name: string;
        OfferNumber: string;
        ClaimNumber: string;
    }): void {
        const { Phone = '', Email = '', Name = '', OfferNumber = '', ClaimNumber = '' } = formData;
        this.consultationCallBackRequestForm = new FormGroup({
            Phone: new FormControl(Phone, [this.serviceProviderContactService.phoneStartsWithValidator.bind(this)]),
            Email: new FormControl(Email, this.commonService.emailValidator),
            Name: new FormControl(Name, [Validators.required]),
            OfferNumber: new FormControl(OfferNumber, null),
            ClaimNumber: new FormControl(ClaimNumber, null)
        });

        if (this.isFromPolicyHolderInfo) {
            this.consultationCallBackRequestForm.get('ClaimNumber').setValidators(Validators.required);
            this.consultationCallBackRequestForm.get('OfferNumber').setValidators(null);
        } else {
            this.consultationCallBackRequestForm.get('ClaimNumber').setValidators(null);
            this.consultationCallBackRequestForm.get('OfferNumber').setValidators(Validators.required);
        }
        this.consultationCallBackRequestForm.updateValueAndValidity();
    }

    public onClose(): void {
        this.matDialog.close(null);
    }

    public isAllowedToCallBackRequest(): boolean {
        const callBackRequest: any = this.consultationCallBackRequestForm.getRawValue();

        if (!callBackRequest.Email && !callBackRequest.Phone) {
            return false;
        }

        return true;
    }

    public async onSendRequest(): Promise<void> {
        this.isLoading = true;
        const formData = this.consultationCallBackRequestForm.getRawValue();
        const command = await this.prepareCallBackRequestCommand(formData);
        this.matDialog.close(command);
    }

    private async prepareCallBackRequestCommand(formData: {
        Phone: string;
        Email: string;
        Name: string;
        OfferNumber: string;
        ClaimNumber: string;
    }): Promise<SendConsultationCallBackRequestCommand> {
        // taking advantage of global this
        const { insuranceCase, caseServiceInfo } = this;

        return this.serviceProviderContactService.prepareCallBackRequestCommand(
            formData,
            insuranceCase.ItemId,
            insuranceCase.Language,
            caseServiceInfo,
            insuranceCase.CaseDocumentList,
            this.isFromPolicyHolderInfo
        );
    }

    private getCurrentConsultation(): {
        Phone: string;
        Email: string;
        Name: string;
        OfferNumber: string;
        ClaimNumber: string;
    } {
        let currentConsultation: any = {};
        this.isFromPolicyHolderInfo = false;

        if (this.data?.policyHolderInfo) {
            this.isFromPolicyHolderInfo = true;
            this.policyHolderInfo = this.data.policyHolderInfo as PolicyHolderDetails;
            currentConsultation.Phone = this.policyHolderInfo.PhoneNumber;
            currentConsultation.Email = this.policyHolderInfo.EmailAddress;
            currentConsultation.Name = this.policyHolderInfo.Name;
            currentConsultation.OfferNumber = '';
            currentConsultation.ClaimNumber = this.insuranceCase.InsuranceClaimNumber;
        } else {
            this.caseServiceInfo = this.data.caseService;
            currentConsultation.Phone = this.caseServiceInfo.ConstructionCompanyPhone;
            currentConsultation.Email = this.caseServiceInfo.ConstructionCompanyEmail;
            currentConsultation.Name = this.caseServiceInfo.ConstructionCompanyContact;
            currentConsultation.OfferNumber = this.caseServiceInfo.UpdatedOfferNumber;
            currentConsultation.ClaimNumber = '';
        }
        return currentConsultation;
    }

    public updateIsAllowedToRequest() {
        this.isAllowedToRequest =
            this.isAllowedToCallBackRequest() &&
            !this.consultationCallBackRequestForm.get('Email')?.hasError('email') &&
            !this.consultationCallBackRequestForm.controls['Phone']?.errors?.['validatePhoneNumber'] &&
            !this.consultationCallBackRequestForm.invalid;
    }

    public formFieldValidationUpdate(): void {
        this.consultationCallBackRequestForm.valueChanges
            .pipe(debounceTime(200), takeUntil(this.destroy$))
            .subscribe(() => {
                this.updateEmailValidation();

                setTimeout(() => {
                    this.updateIsAllowedToRequest();
                });
            });
        this.updateEmailValidation();
        this.updateIsAllowedToRequest();
    }

    public updateEmailValidation(): void {
        const emailControl = this.consultationCallBackRequestForm.get('Email');
        const phoneControl = this.consultationCallBackRequestForm.get('Phone');
        if (!emailControl || !phoneControl) {
            return;
        }
        if (!emailControl.value && !phoneControl.value) {
            emailControl.setValidators([Validators.required, this.commonService.emailValidator]);
        } else if (phoneControl.value) {
            emailControl.setValidators([this.commonService.emailValidator]);
        }
        emailControl.updateValueAndValidity({ emitEvent: false });
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}
